import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AuthStatusCode, EmailValidationResponseStatus } from '@as-react/common-types';
import { useAemConfig } from '@as-react/common-components';
import { customerQueries, queryClient } from '@as-react/common-queries';
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { validateEmailWithBackend, EMAIL_VALIDATION_DEBOUNCE } from '../../../email/EmailValidationUtil';
import FormValidationUtil from '../../../../util/FormValidationUtil';
import { ValueChangeWatcher } from '../../../form/watchers/ValueChangeWatcher';
import { authActions } from '../../../../redux/authentication/actions/AuthActions.creators';
import { makeGetErrorGuestState } from '../../../../redux/authentication/selectors/AuthSelectors';
import { ShoplandingLoginForm } from './ShoplandingLoginForm';
import { debouncedPromise } from '@as-react/common-utils';
const defaultCardInfo = {
    customerUUID: null,
    email: null,
    validEmail: false,
    existingCustomer: false,
};
export const ShoplandingLogin = () => {
    const { defaultRequestParams, servicesEndpoint, checkoutLoyaltyCardPrefix } = useAemConfig();
    const { t } = useTranslation([I18N_NAMESPACES.checkout, I18N_NAMESPACES.buy, I18N_NAMESPACES.account]);
    const [customerCardKnown, setCustomerCardKnown] = useState(false);
    const [customerCardInfo, setCustomerCardInfo] = useState(defaultCardInfo);
    const [linkedEmail, setLinkedEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [emailOrCardCodeFieldValue, setEmailOrCardCodeFieldValue] = useState();
    const [emailOrCardCodeStatus, setEmailOrCardCodeStatus] = useState();
    const [linkedEmailStatus, setLinkedEmailStatus] = useState();
    const [linkedEmailFieldValue, setLinkedEmailFieldValue] = useState();
    const [suggestedEmail, setSuggestedEmail] = useState();
    const [skipEmailValidation, setSkipEmailValidation] = useState(false);
    const dispatch = useDispatch();
    const guestError = useSelector(makeGetErrorGuestState());
    const errorFeedback = guestError ? t(guestError) : undefined;
    const { getCardNumberExists, getEmailExists } = customerQueries({ servicesEndpoint, defaultRequestParams });
    const getCustomerInfoForExploreMoreCard = (customerCardNumber) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            return yield queryClient.fetchQuery(getCardNumberExists(customerCardNumber));
        }
        catch (_a) {
            return defaultCardInfo;
        }
    });
    const getEmailStatus = (email) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            return yield queryClient.fetchQuery(getEmailExists({ email }));
        }
        catch (_a) {
            return { accountStatusCode: AuthStatusCode.NO_ACCOUNT, knownForFascia: false, hasIdentity: false };
        }
    });
    const validateEmailOrCardCode = useCallback(debouncedPromise((emailOrCardCode, context) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (skipEmailValidation) {
            return true;
        }
        if (emailOrCardCode) {
            const { isValidFormat, isDefaultCardNumber } = FormValidationUtil.isValidExploreMoreCardFormat(emailOrCardCode, checkoutLoyaltyCardPrefix);
            if (isValidFormat && isDefaultCardNumber) {
                return context.createError({
                    message: t(`${I18N_NAMESPACES.checkout}:checkout.login.error.loyalty.card.default`),
                });
            }
            else if (isValidFormat && !isDefaultCardNumber) {
                const cardInfo = yield getCustomerInfoForExploreMoreCard(emailOrCardCode);
                setCustomerCardInfo(cardInfo);
                setCustomerCardKnown(cardInfo.existingCustomer);
                if (!cardInfo.existingCustomer) {
                    return context.createError({
                        message: t(`${I18N_NAMESPACES.checkout}:checkout.login.error.loyalty.card`),
                    });
                }
                else if (cardInfo.validEmail) {
                    setLinkedEmail((_a = cardInfo.email) !== null && _a !== void 0 ? _a : undefined);
                }
                else if (!cardInfo.validEmail) {
                    setLinkedEmail('');
                }
            }
            else if (!isValidFormat) {
                if (!FormValidationUtil.isValidEmailPattern(emailOrCardCode)) {
                    return context.createError({ message: t(`${I18N_NAMESPACES.buy}:buy.error.invalid`) });
                }
                else {
                    return yield validateEmailWithBackend(emailOrCardCode, context, servicesEndpoint, emailOrCardCodeStatus, setSuggestedEmail, setEmailOrCardCodeStatus);
                }
            }
        }
        else if (!emailOrCardCode) {
            setCustomerCardKnown(false);
            return context.createError({ message: t(`${I18N_NAMESPACES.buy}:buy.login.error.required`) });
        }
        return true;
    }), EMAIL_VALIDATION_DEBOUNCE), [customerCardKnown, skipEmailValidation, emailOrCardCodeStatus]);
    const validateLinkedEmail = useCallback(debouncedPromise((tmpLinkedEmail, context) => __awaiter(void 0, void 0, void 0, function* () {
        if (skipEmailValidation) {
            return true;
        }
        if (!tmpLinkedEmail) {
            return context.createError({ message: t(`${I18N_NAMESPACES.buy}:buy.login.email.error.required`) });
        }
        else if (!FormValidationUtil.isValidEmailPattern(tmpLinkedEmail)) {
            return context.createError({
                message: EmailValidationResponseStatus.INVALID,
            });
        }
        else if (linkedEmail !== tmpLinkedEmail) {
            const { hasIdentity } = yield getEmailStatus(tmpLinkedEmail);
            if (hasIdentity) {
                return context.createError({
                    message: t(`${I18N_NAMESPACES.checkout}:checkout.login.error.known.user`),
                });
            }
            return yield validateEmailWithBackend(tmpLinkedEmail, context, servicesEndpoint, linkedEmailStatus, setSuggestedEmail, setLinkedEmailStatus);
        }
        return true;
    }), EMAIL_VALIDATION_DEBOUNCE), [customerCardKnown, skipEmailValidation, linkedEmail]);
    const emailValidationSchema = Yup.object({
        emailOrCardCode: Yup.string().default('').test(validateEmailOrCardCode),
        linkedEmail: customerCardKnown
            ? Yup.string().default('').test(validateLinkedEmail)
            : Yup.string().default('').optional(),
    });
    const handleEmailOrCardCodeChange = (email) => {
        setEmailOrCardCodeFieldValue(email);
        skipEmailValidation && setSkipEmailValidation(false);
        linkedEmail && setLinkedEmail('');
        customerCardInfo && setCustomerCardInfo(defaultCardInfo);
        customerCardKnown && setCustomerCardKnown(false);
    };
    const handleLinkedEmailChange = (email) => {
        setLinkedEmailFieldValue(email);
        skipEmailValidation && setSkipEmailValidation(false);
    };
    const handleIgnoreSuggestedEmailClick = () => {
        setSkipEmailValidation(true);
    };
    const handleAcceptSuggestedEmailClick = () => {
        if (customerCardKnown) {
            setLinkedEmailStatus(`${EmailValidationResponseStatus.VALID}_${suggestedEmail}`);
            setLinkedEmailFieldValue(suggestedEmail);
        }
        else {
            setEmailOrCardCodeStatus(`${EmailValidationResponseStatus.VALID}_${suggestedEmail}`);
            setEmailOrCardCodeFieldValue(suggestedEmail);
        }
    };
    const handleShopLoginSubmit = ({ linkedEmail, emailOrCardCode }) => {
        var _a;
        if (FormValidationUtil.isValidExploreMoreCardFormat(emailOrCardCode, checkoutLoyaltyCardPrefix).isValidFormat &&
            !linkedEmail)
            return;
        setIsLoading(true);
        if (customerCardKnown) {
            dispatch(authActions.createLoginGuestAction(linkedEmail, emailOrCardCode, (_a = customerCardInfo.customerUUID) !== null && _a !== void 0 ? _a : undefined));
        }
        else {
            dispatch(authActions.createLoginGuestAction(emailOrCardCode));
        }
    };
    useEffect(() => {
        if (errorFeedback && isLoading) {
            setIsLoading(false);
        }
    }, [errorFeedback]);
    useEffect(() => {
        setLinkedEmailFieldValue(linkedEmail);
    }, [linkedEmail]);
    return (_jsxs(ShoplandingLoginForm, { validationSchema: emailValidationSchema, suggestedEmail: suggestedEmail, errorFeedback: errorFeedback, customerCardKnown: customerCardKnown, isLoading: isLoading, onIgnoreSuggestedEmailClick: handleIgnoreSuggestedEmailClick, onAcceptSuggestedEmailClick: handleAcceptSuggestedEmailClick, onSubmit: handleShopLoginSubmit, children: [_jsx(ValueChangeWatcher, { fieldName: "emailOrCardCode", value: emailOrCardCodeFieldValue, skipValidation: skipEmailValidation, onChange: handleEmailOrCardCodeChange, onError: setEmailOrCardCodeStatus }), _jsx(ValueChangeWatcher, { fieldName: "linkedEmail", value: linkedEmailFieldValue, skipValidation: skipEmailValidation, onChange: handleLinkedEmailChange, onError: setLinkedEmailStatus })] }));
};
